import { useSelector } from "react-redux";
import {
  bulkFileOwnerServicechange,
  getBidderTypeLabel,
  getAuctionEventById,
  getMultipleFilesByOwnerIdentifier,
  getTeamMembers,
  NON_INDIVIDUAL_BIDDER_TYPES,
  updateBidderForEvent,
} from "services";
import { DateTime } from "luxon";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { useEffect, useState } from "react";
import {
  addBidderForEvent,
  fetchIDCardDetails,
  getBidderByUuid,
  handleBackendValidationsForBidderCreation,
  prefillDetailsByAttribute,
} from "services/bidder";
import createJointBidderPayload from "../JointBidder/createJointBidderPayload";
import { useStateLogger } from "../../../../hoc";
import { NonIndividualBidderType } from "../NonIndividual/NonIndividualBidderFormV2";

const isDigioPanFullVerificationEnabled =
  process.env.NEXT_PUBLIC_DIGIO_PAN_FULL_VERIFICATION_ENABLED === "true";

const mobileNumberRegEx: RegExp = /^[0-9]{10}$/;
const panRegex: RegExp = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const aadharRegex: RegExp = /^(?:[2-9][0-9]{11})?$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{3,}$/;
const EMAIL_DEBOUNCE_MS = 500;
const non_individual_types: string[] = NON_INDIVIDUAL_BIDDER_TYPES;

let timeoutId: number | undefined;
let abortCtrl: AbortController | null = null;

const useGetBasicDetails = () => {
  const banker = useSelector((state: any) => {
    return state.bankerAuth.user;
  });

  const currentBranch = useSelector((state: any) => {
    return state.bankerAuth.currentBranch;
  });

  const bank = useSelector((state: any) => {
    return state.bankerAuth.bank;
  });
  return {
    banker,
    currentBranch,
    bank,
  };
};

const getTeamMembersEmailDetails = async (
  currentBranch: any,
  setMembersEmail: any,
) => {
  let response: any = await getTeamMembers(currentBranch?.team["teams*uuid"]);
  const filteredData = response.data
    .map((item: any) => {
      const emailAttr = item?.attributes?.find(
        (attr: any) => attr.type === "email",
      );
      return emailAttr ? emailAttr.value : null;
    })
    .filter(Boolean);
  setMembersEmail(filteredData);
};

export type AddBidderFormType =
  | "individual"
  | NonIndividualBidderType
  | "joint";

const useInitializeBetaForms = (
  bidderData: any,
  membersEmail: any,
  mode: string,
  setIsNameVerified: any,
  bidderType: AddBidderFormType,
  activeStep?: number,
) => {
  const price =
    bidderData?.initial_bid_price ?? bidderData?.meta?.initial_bid_price;

  const allowPrice =
    (bidderType === "joint" && activeStep === 0) ||
    bidderType === "individual" ||
    non_individual_types.includes(bidderType);

  const getUserTypeOption = (value?: string) => {
    if (!value) return "";
    const label = getBidderTypeLabel(value) || value;
    return {
      label,
      value,
    };
  };

  const form = useBetaForm({
    email: bidderData ? bidderData.email : "",
    name: bidderData
      ? bidderData?.meta?.name
        ? bidderData?.meta?.name
        : ""
      : "",
    pan: bidderData ? (bidderData?.meta?.pan ? bidderData?.meta?.pan : "") : "",
    kyc_verified: bidderData ? bidderData.kyc_verified : false,
    emd_amount_paid: bidderData ? bidderData.emd_amount_paid : false,
    agreement_signed: bidderData ? bidderData.agreement_signed : false,
    gst_number: bidderData
      ? bidderData?.meta?.user_details?.gst_number
        ? bidderData?.meta?.user_details?.gst_number
        : ""
      : "",
    application_form_filled: bidderData
      ? bidderData.application_form_filled
      : false,
    aadhar_number: bidderData
      ? bidderData?.meta?.user_details?.aadhar_number
        ? bidderData?.meta?.user_details?.aadhar_number
        : ""
      : "",
    mobile_number: bidderData
      ? bidderData?.meta?.user_details?.mobile_number
        ? bidderData?.meta?.user_details?.mobile_number
        : ""
      : "",
    address: bidderData
      ? bidderData?.meta?.user_details?.address
        ? bidderData?.meta?.user_details?.address
        : ""
      : "",
    user_type:
      getUserTypeOption(bidderType) ||
      (bidderData
        ? bidderData?.meta?.user_details?.user_type
          ? bidderData?.meta?.user_details?.user_type
          : ""
        : ""),
    ...(price && allowPrice ? { initial_bid_price: price } : {}),
  });
  useStateLogger("🚀 ~ initializeBetaForms ~ form:", form?.value);

  const [formConstraints, setFormConstraints] = useState({
    email: {
      presence: {
        allowEmpty: false,
      },
      email: true,
      exclusion: membersEmail,
    },
    name: {
      presence: {
        allowEmpty: false,
      },
    },
    pan: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: panRegex,
        message: "^PAN number is not valid.",
      },
    },
    aadhar_number: {
      presence: {
        allowEmpty: true,
      },
      format: {
        pattern: aadharRegex,
        message: "^Aadhar number is not valid.",
      },
    },
    gst_number: {
      presence: {
        allowEmpty: true,
      },
    },
    mobile_number: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: mobileNumberRegEx,
        message: "^Mobile number is not valid.",
      },
    },
    address: {
      presence: {
        allowEmpty: true,
      },
    },
    user_type: {
      presence: {
        allowEmpty: false,
      },
    },
    initial_bid_price: {},
  });

  useEffect(() => {
    const price = form.value?.initial_bid_price;

    setFormConstraints((prev: any) => {
      const updated = { ...prev };

      if (price !== "" && price !== null && price !== undefined) {
        updated.initial_bid_price = {
          presence: { allowEmpty: false },
          numericality: {
            greaterThanOrEqualTo: 0,
            message: "^Initial bid price must be a non-negative number.",
          },
        };

        updated.initial_bid_price_confirmed = {
          presence: { allowEmpty: false },
          inclusion: {
            within: [true],
            message:
              "^Please tick the checkbox below the offer price field to confirm the amount.",
          },
        };
      } else {
        delete updated.initial_bid_price;
        delete updated.initial_bid_price_confirmed;
      }

      return updated;
    });
  }, [form.value?.initial_bid_price]);

  useEffect(() => {
    form.setValidationRules(formConstraints);
  }, [membersEmail, formConstraints]);

  useEffect(() => {
    if (!bidderData) return;

    if (mode === "joint-creation") {
      setIsNameVerified(bidderData?.name ? true : false);
    }
    form.setField("email", bidderData?.email || "");
    form.setField(
      "name",
      mode === "joint-creation"
        ? bidderData?.name
        : bidderData?.meta?.name || "",
    );
    form.setField(
      "pan",
      mode === "joint-creation" ? bidderData?.pan : bidderData?.meta?.pan || "",
    );
    form.setField("kyc_verified", bidderData?.kyc_verified ?? false);
    form.setField("emd_amount_paid", bidderData?.emd_amount_paid ?? false);
    form.setField("agreement_signed", bidderData?.agreement_signed ?? false);
    form.setField(
      "application_form_filled",
      bidderData?.application_form_filled ?? false,
    );
    const userDetails =
      mode === "joint-creation"
        ? bidderData
        : bidderData?.meta?.user_details || {};
    form.setField("aadhar_number", userDetails.aadhar_number || "");
    form.setField("gst_number", userDetails.gst_number || "");
    form.setField("mobile_number", userDetails.mobile_number || "");
    form.setField("address", userDetails.address || "");

    if (allowPrice) {
      form.setField("initial_bid_price", price ?? "");

      if (price !== undefined && price !== null && price !== "") {
        form.setField("initial_bid_price_confirmed", true);
      }
    }

    if (getUserTypeOption(bidderType)) {
      form.setField("user_type", getUserTypeOption(bidderType));
    } else {
      form.setField("user_type", userDetails?.user_type || "");
    }
    if (
      bidderData?.errors &&
      bidderData?.errors?.email &&
      bidderData?.errors?.mobile_number
    ) {
      form.setErrors({
        email: [bidderData?.errors?.email],
        mobile_number: [bidderData?.errors?.mobile_number],
      });
    }
  }, [bidderData, bidderType, mode]);

  return form;
};

const handlePANVerification = async (
  panData: any,
  form: any,
  setIsNameVerified: any,
) => {
  if (panData) {
    const userEnteredName = (form.getField("name") || "").toString().trim();

    const isPanStatusValid = isDigioPanFullVerificationEnabled
      ? panData?.status?.toLowerCase() === "valid"
      : panData?.status === "VALID";

    const isNameLongEnough = userEnteredName.length >= 3;
    const isNameMatch = isDigioPanFullVerificationEnabled
      ? isNameLongEnough && panData?.name_as_per_pan_match === true
      : true;

    if (
      panData?.category?.toLowerCase() !==
      form.getField("user_type")?.value?.toLowerCase()
    ) {
      form.pushError({
        pan: [
          form.getField("user_type")?.value?.toLowerCase() === "company"
            ? "Please enter valid Company PAN"
            : "Please enter valid PAN based on bidder type",
        ],
      });
      if (!isDigioPanFullVerificationEnabled) {
        form.setField("name", "");
      }
      setIsNameVerified(false);
    } else if (!isPanStatusValid) {
      form.pushError({
        pan: ["PAN seems invalid. Please check the number and try again."],
      });
      if (!isDigioPanFullVerificationEnabled) {
        form.setField("name", "");
      }
      setIsNameVerified(false);
    } else if (!isNameMatch) {
      if (!isNameLongEnough) {
        form.pushError({
          pan: [
            "Please enter name (minimum 3 characters) before PAN verification.",
          ],
        });
      } else {
        form.pushError({
          pan: [
            "The name doesn't match PAN records. Please enter name exactly as on PAN card.",
          ],
        });
      }
      setIsNameVerified(false);
    } else {
      form.pushError({ pan: [] });
      if (!isDigioPanFullVerificationEnabled) {
        form.setField("name", panData?.full_name);
      }
      setIsNameVerified(true);
    }
  } else {
    form.setField("name", "");
    form.setField("pan", "");
    form.pushError({ pan: ["Please enter pan number"] });
    setIsNameVerified(false);
  }
};

const handleAcceptBidder = async (id: string | undefined | string[]) => {
  if (!id) return;

  try {
    const data: any = await updateBidderForEvent({
      uuid: id,
      status: "verified",
      kyc_verified: true,
      emd_amount_paid: true,
      agreement_signed: true,
      application_form_filled: true,
    });
    if (data) window.history.back();
  } catch (err) {
    console.log("error", err);
  }
};

const handleRejectBidder = async (
  cancellationReason: string,
  id: string | undefined | string[],
  setIsLoading: any,
) => {
  try {
    if (id === "") return;
    setIsLoading(true);
    const data: any = await updateBidderForEvent({
      uuid: id,
      status: "rejected",
      remarks: cancellationReason,
    });
    if (data) {
      window.location.reload();
    }
    setIsLoading(false);
  } catch (error: any) {
    setIsLoading(false);
    console.log("check here error", error);
  }
};

const kycDetailsSwitchFields = [
  { title: "KYC Verified", value: "kyc_verified" },
  { title: "EMD Amount Paid", value: "emd_amount_paid" },
  { title: "Agreement Signed", value: "agreement_signed" },
  { title: "Application Form Filled", value: "application_form_filled" },
];

const handlePANFieldValidations = async (
  e: any,
  form: any,
  setIsFetchingPan: any,
  setIsNameVerified: any,
) => {
  let value = (e?.target?.value || "").toString().trim().toUpperCase();

  // Always save the PAN value first
  form.setField("pan", value);

  // Only verify if we have a valid PAN (10 chars) matching regex
  if (value.length >= 10 && panRegex.test(value)) {
    try {
      setIsFetchingPan(true);

      const payload: any = {
        card_type: "PAN",
        card_number: value,
      };

      // V4 mode: require name >= 3 chars before calling API (onChange)
      const userName = (form.getField("name") || "").toString().trim();
      if (isDigioPanFullVerificationEnabled && userName.length < 3) {
        form.pushError({
          pan: [
            "Please enter name (minimum 3 characters) before PAN verification.",
          ],
        });
        setIsFetchingPan(false);
        return;
      }

      // Add name if full verification is enabled AND name has at least 3 chars
      // Otherwise, don't send name to allow legacy behavior
      if (isDigioPanFullVerificationEnabled && userName.length >= 3) {
        payload.name = userName.toUpperCase();
      }

      const res: any = await fetchIDCardDetails(payload);
      setIsFetchingPan(false);
      await handlePANVerification(res, form, setIsNameVerified);
    } catch (error: any) {
      console.log("error:here ", error);

      // Handle 422 errors with Digio error messages
      if (error?.response?.status === 422) {
        const errorMessage =
          error?.response?.data?.message?.message ||
          "Validation failed. Please check your details.";
        form.pushError({
          pan: [errorMessage],
        });
      } else {
        form.pushError({
          pan: ["An internal error occurred. Please try again later."],
        });
      }
      // Legacy mode: clear name from API error
      // V4 mode: keep user's entered name
      if (!isDigioPanFullVerificationEnabled) {
        form.setField("name", "");
      }
      setIsFetchingPan(false);
      setIsNameVerified(false);
    }
  }
};

const focusFieldById = (id: any) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.focus({ preventScroll: true });
};

const handleBidderDataSubmit = async (
  form: any,
  mode: any,
  id: any,
  for_event: any,
  banker: any,
  bank: any,
  currentBranch: any,
  setIsErrorHandlingModalOpen: any,
  setErrorHandlingModalData: any,
  randomId: any,
  kycFiles: any,
  isOverwrite: any,
  router: any,
) => {
  try {
    form.value.email = form.value.email.trim().toLowerCase();
    if (form.validate()) {
      form.startProcessing();
      if (mode === "edit") {
        try {
          let payload = form.value;
          payload["uuid"] = id;
          if (
            payload.kyc_verified &&
            payload.emd_amount_paid &&
            payload.agreement_signed &&
            payload.application_form_filled
          ) {
            payload["status"] = "verified";
          } else {
            payload["status"] = "pending";
          }

          const eventData: any = await getAuctionEventById(for_event);
          console.log("event::1234::>", eventData, for_event);
          payload["meta"] = {
            banker_email: banker?.attributes[0]?.value,
            bank_name: bank?.name,
            user_details: {
              address: payload["address"],
              mobile_number: payload["mobile_number"],
              aadhar_number: payload["aadhar_number"],
              user_type: payload["user_type"],
              gst_number: payload["gst_number"],
            },
            approver_email:
              eventData?.meta?.approver && eventData?.meta?.approver.length > 0
                ? eventData?.meta?.approver[0]?.email
                : "",
            approver_name:
              eventData?.meta?.approver && eventData?.meta?.approver.length > 0
                ? eventData?.meta?.approver[0]?.name
                  ? eventData?.meta?.approver[0]?.name
                  : eventData?.meta?.approver[0]?.email
                : "",
            branch_name: currentBranch?.label,
            event_name: eventData?.event_name,
            dashboard_url: `${process.env.NEXT_PUBLIC_BIDDER_DB_URL}/auction-events`,
            event_id: eventData.listing_id,
            auction_start_datetime: DateTime.fromISO(eventData?.start_date_time)
              .setZone("Asia/Kolkata")
              .toFormat("dd MMMM yyyy hh:mm a"),
            emd_last_datetime: DateTime.fromISO(
              eventData?.property_dump?.emd_last_date,
            )
              .setZone("Asia/Kolkata")
              .toFormat("dd MMMM yyyy hh:mm a"),
            name: payload.name,
            pan: payload.pan,
            ...(payload.initial_bid_price && {
              initial_bid_price: payload.initial_bid_price,
            }),
          };
          payload["digio_request_status"] = "approved";
          const res = await updateBidderForEvent(payload);
          if (res) {
            router?.push(`/dashboard/bidders?for_event=${for_event}`);
          }
          return true;
        } catch (error: any) {
          console.log("error::", error);
          if (error?.response?.data?.errors) {
            form.setErrors(error.response.data.errors);
          }

          const errorDetails =
            error?.response?.data?.message?.errorDetails ||
            error?.response?.data?.errorDetails;

          if (errorDetails) {
            let emailError = errorDetails?.email;
            let mobileError = errorDetails?.mobile;

            if (emailError && mobileError)
              form.setErrors({
                email: [emailError],
                mobile_number: [mobileError],
              });
            else if (emailError)
              form.setErrors({
                email: [emailError],
              });
            else if (mobileError)
              form.setErrors({
                mobile_number: [mobileError],
              });
          }

          return false;
        }
      } else {
        try {
          let payload = form?.value;

          if (
            payload.kyc_verified &&
            payload.emd_amount_paid &&
            payload.agreement_signed &&
            payload.application_form_filled
          ) {
            payload["status"] = "verified";
          } else {
            payload["status"] = "pending";
          }

          const eventData: any = await getAuctionEventById(for_event);

          payload["meta"] = {
            banker_email: banker?.attributes[0]?.value,
            bank_name: bank?.name,
            user_details: {
              address: payload["address"],
              mobile_number: payload["mobile_number"],
              aadhar_number: payload["aadhar_number"],
              user_type: payload["user_type"],
              gst_number: payload["gst_number"],
            },
            approver_email:
              eventData?.meta?.approver && eventData?.meta?.approver.length > 0
                ? eventData?.meta?.approver[0]?.email
                : "",
            approver_name:
              eventData?.meta?.approver && eventData?.meta?.approver.length > 0
                ? eventData?.meta?.approver[0]?.name
                  ? eventData?.meta?.approver[0]?.name
                  : eventData?.meta?.approver[0]?.email
                : "",
            branch_name: currentBranch?.label,
            event_name: eventData?.event_name,
            dashboard_url: `${process.env.NEXT_PUBLIC_BIDDER_DB_URL}/auction-events`,
            event_id: eventData.listing_id,
            auction_start_datetime: DateTime.fromISO(eventData?.start_date_time)
              .setZone("Asia/Kolkata")
              .toFormat("dd MMMM yyyy hh:mm a"),
            emd_last_datetime: DateTime.fromISO(
              eventData?.property_dump?.emd_last_date,
            )
              .setZone("Asia/Kolkata")
              .toFormat("dd MMMM yyyy hh:mm a"),
            name: payload.name,
            pan: payload.pan,
            ...(payload.initial_bid_price && {
              initial_bid_price: payload.initial_bid_price,
            }),
          };
          payload["digio_request_status"] = "approved";
          payload["event_uuid"] = eventData?.uuid;
          if (isOverwrite) {
            payload["is_overridden"] = true;
          }

          const res: any = await addBidderForEvent(payload);
          if (res) {
            let uuid = res?.uuid;

            if (!uuid || kycFiles?.length === 0) {
              router?.push(`/dashboard/bidders?for_event=${for_event}`);
            }
            const payload = {
              older_id: randomId,
              newer_id: uuid,
            };
            const idChangeRes = await bulkFileOwnerServicechange(payload);
            if (idChangeRes) {
              router?.push(`/dashboard/bidders?for_event=${for_event}`);
            }
          }
          return true;
        } catch (error: any) {
          console.log("error", error);
          if (error?.response?.data?.errors) {
            form.setErrors(error.response.data.errors);
          }

          const errorDetails =
            error?.response?.data?.message?.errorDetails ||
            error?.response?.data?.errorDetails;

          if (errorDetails) {
            let emailError = errorDetails?.email;
            let mobileError = errorDetails?.mobile;

            if (emailError && mobileError) {
              form.setErrors({
                email: [emailError],
                mobile_number: [mobileError],
              });
              focusFieldById("name");
              setErrorHandlingModalData([
                {
                  email: emailError,
                  mobile_number: mobileError,
                },
              ]);
              setIsErrorHandlingModalOpen({ open: true, type: "api" });
            } else if (emailError) {
              form.setErrors({
                email: [emailError],
              });
              focusFieldById("name");
              setErrorHandlingModalData([
                {
                  email: emailError,
                },
              ]);
              setIsErrorHandlingModalOpen({ open: true, type: "api" });
            } else if (mobileError) {
              form.setErrors({
                mobile_number: [mobileError],
              });
              focusFieldById("mobile_number");
              setErrorHandlingModalData([
                {
                  mobile_number: mobileError,
                },
              ]);
              setIsErrorHandlingModalOpen({ open: true, type: "api" });
            }
          }
          return false;
        }
      }
    }
  } catch (error: any) {
    form.finishProcessing();
    console.log("check here error", error);
    if (error?.response?.data?.errors) {
      form.setErrors(error.response.data.errors);
    }
  }
};

const validateBidders = (
  allBiddersData: any,
  setErrorHandlingModalData: any,
): boolean => {
  let isValid = true;

  const emailIdx = new Map<string, number>();
  const mobileIdx = new Map<string, number>();
  const panIdx = new Map<string, number>();

  allBiddersData.forEach((b: any, idx: number) => {
    b.errors = {};
    if (!b.email) {
      b.errors.email = "Bidder Email is required";
      isValid = false;
    }
    if (!b.mobile_number) {
      b.errors.mobile_number = "Bidder Mobile number is required";
      isValid = false;
    }
    if (!b.pan) {
      b.errors.pan = "Bidder PAN is required";
      isValid = false;
    }

    if (b.email) {
      if (emailIdx.has(b.email)) {
        b.errors.email = "Email must be unique";
        allBiddersData[emailIdx.get(b.email)!].errors!.email =
          "Email must be unique";
        isValid = false;
      } else {
        emailIdx.set(b.email, idx);
      }
    }

    if (b.mobile_number) {
      if (mobileIdx.has(b.mobile_number)) {
        b.errors.mobile_number = "Mobile number must be unique";
        allBiddersData[mobileIdx.get(b.mobile_number)!].errors!.mobile_number =
          "Mobile number must be unique";
        isValid = false;
      } else {
        mobileIdx.set(b.mobile_number, idx);
      }
    }
  });

  if (!isValid) {
    const allErrors = allBiddersData.map((b: any, idx: number) => ({
      index: idx,
      bidderLabel:
        idx === 0 ? "Primary Bidder Details" : `Bidder ${idx + 1} Details`,
      ...b.errors,
    }));
    setErrorHandlingModalData(allErrors);
  }

  return isValid;
};

const handleSubmitAllBiddersData = async (
  for_event: string | undefined | string[],
  allBiddersData: any,
  setActiveStep: any,
  setErrorHandlingModalData: any,
  setIsErrorHandlingModalOpen: any,
  banker: any,
  bank: any,
  currentBranch: any,
  isOverwrite: any,
  activeStep: any,
  kycFiles: any,
  jointDeclarationFiles: any,
  randomId: any,
  jointDeclarationRandomId: any,
) => {
  try {
    const eventData: any = await getAuctionEventById(for_event);
    if (
      !validateBidders(allBiddersData, setErrorHandlingModalData) &&
      setActiveStep
    ) {
      console.log("Invalid bidders data", allBiddersData);
      setIsErrorHandlingModalOpen({ open: true, type: "validation" });
      setActiveStep(0);
      return;
    }
    let primaryBidderIndex = 0;
    const payload = await createJointBidderPayload(
      eventData,
      allBiddersData,
      banker,
      bank,
      currentBranch,
      primaryBidderIndex,
      isOverwrite,
    );
    console.log("payload: ", payload);
    const res: any = await addBidderForEvent(payload);

    if (!res?.uuid) {
      window.history.back();
      return;
    }
    await Promise.all([
      kycFiles?.length
        ? bulkFileOwnerServicechange({
            older_id: randomId,
            newer_id: res.uuid,
          })
        : null,
      jointDeclarationFiles?.length
        ? bulkFileOwnerServicechange({
            older_id: jointDeclarationRandomId,
            newer_id: res.uuid,
          })
        : null,
    ]);
    window.history.back();
  } catch (err: any) {
    console.error(
      "Error in joint-creation",
      err?.response?.status,
      err?.response?.data,
    );
    if (
      err?.response?.status === 422 &&
      err?.response?.data?.message ===
        "Validation failed. Email already added for this event." &&
      setActiveStep
    ) {
      const existingEmails =
        err?.response?.data?.errorDetails?.existingEmails || [];
      const errorData: any[] = [];

      allBiddersData.forEach((bidder: any, idx: number) => {
        const bidderEmail = bidder?.email?.toLowerCase();
        if (
          bidderEmail &&
          existingEmails.some(
            (existingEmail: string) =>
              existingEmail?.toLowerCase() === bidderEmail,
          )
        ) {
          errorData.push({
            index: idx,
            bidderLabel:
              idx === 0
                ? "Primary Bidder Details"
                : `Bidder ${idx + 1} Details`,
            email: "Email already added for this event.",
          });
        }
      });

      if (errorData.length > 0) {
        setErrorHandlingModalData(errorData);
        setIsErrorHandlingModalOpen({ open: true, type: "validation" });
        setActiveStep(0);
        return;
      }
    }
    if (
      err?.response?.status === 422 &&
      err?.response?.data?.message?.message === "Identity conflict" &&
      setActiveStep
    ) {
      setErrorHandlingModalData([
        {
          index: 0,
          ...err?.response?.data?.message?.errorDetails,
        },
      ]);
    }
    if (
      err?.response?.status === 422 &&
      err?.response?.data?.message ===
        "Identity conflict(s) in joint bidders" &&
      setActiveStep
    ) {
      const data = err?.response?.data?.errors.filter(
        (detail: any) => detail?.errorDetails,
      );
      const errorData = [];
      for (let i = 0; i < data?.length; i++) {
        errorData.push({
          email: data[i].errorDetails.email,
          mobile_number: data[i].errorDetails.mobile,
        });
      }

      setErrorHandlingModalData(errorData || []);
    }
    setIsErrorHandlingModalOpen({ open: true, type: "api" });
  }
};

const bidderDataInitialise = async (
  id: string | undefined | string[],
  setBidderData: any,
  setKycFiles: any,
  setJointDeclarationFiles: any,
  setRenderKey: any,
) => {
  let data: any;
  try {
    data = await getBidderByUuid(id);
  } catch (err) {
    console.error("bidderDataInitialise: failed to fetch bidder", err);
    return null;
  }
  setBidderData(data);

  if (data) {
    const kycData: any = await getMultipleFilesByOwnerIdentifier(
      typeof id === "string" ? id : "",
      data?.created_by === "banker"
        ? "kyc-documents"
        : "signed-bid-request-agreement",
    );

    if (kycData) {
      const filteredKycdata = kycData?.map((item: any) => ({
        id: item?.uuid,
        mime: item?.mime,
        filename: item?.file_name,
      }));
      setKycFiles(filteredKycdata);
      setRenderKey((prev: any) => prev + 1);
    }

    if (data?.is_joint && data?.access_type === "primary") {
      const jointDeclarationData: any = await getMultipleFilesByOwnerIdentifier(
        typeof id === "string" ? id : "",
        "joint-declaration-documents",
      );

      if (jointDeclarationData) {
        const filteredJointDeclarationData = jointDeclarationData?.map(
          (item: any) => ({
            id: item?.uuid,
            mime: item?.mime,
            filename: item?.file_name,
          }),
        );
        setJointDeclarationFiles(filteredJointDeclarationData);
        setRenderKey((prev: any) => prev + 1);
      }
    }
  }

  return data;
};

const saveCurrentBidderDataHelper = (
  form: any,
  setCurrentBidderData: any,
  activeStep: any,
  bidders_count: any,
) => {
  if (
    setCurrentBidderData &&
    activeStep !== undefined &&
    activeStep >= 0 &&
    activeStep < Number(bidders_count || 0)
  ) {
    const currentFormData = {
      name: form.getField("name"),
      email: form.getField("email"),
      pan: form.getField("pan"),
      mobile_number: form.getField("mobile_number"),
      aadhar_number: form.getField("aadhar_number"),
      address: form.getField("address"),
      user_type: form.getField("user_type"),
      kyc_verified: form.getField("kyc_verified"),
      emd_amount_paid: form.getField("emd_amount_paid"),
      agreement_signed: form.getField("agreement_signed"),
      application_form_filled: form.getField("application_form_filled"),
    };

    setCurrentBidderData((prevData: any) => {
      const updatedData = [...(prevData || [])];
      updatedData[activeStep] = currentFormData;
      return updatedData;
    });
  }
};

const handleJointParticipationValidation = async (
  form: any,
  setActiveStep: any,
  activeStep: any,
  saveCurrentBidderData: any,
  setIsNameVerified: any,
) => {
  if (form.validate() && setActiveStep && (activeStep === 0 || activeStep)) {
    // Save current bidder data before moving to next step
    saveCurrentBidderData();
    setIsNameVerified(false);
    setActiveStep(activeStep + 1);
  }
};

const prefillDetailsByEmail = async (
  e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  form: any,
  disablePan: boolean,
  setDisablePan: (value: boolean) => void,
  setIsNameVerified: (value: boolean) => void,
  bidder_type: string,
  setNotificationPopup: any,
) => {
  const email = e.target.value;
  form.setField("email", email);

  if (timeoutId) window.clearTimeout(timeoutId);

  if (!email) {
    setDisablePan(false); // no email → enable PAN
    return;
  }

  timeoutId = window.setTimeout(async () => {
    // Clear prefilled fields before API call to prevent old data persisting
    form.setField("mobile_number", "");
    if (non_individual_types.includes(bidder_type))
      form.setField("gst_number", "");
    else form.setField("aadhar_number", "");
    form.setField("address", "");
    form.setField("pan", "");
    form.setField("name", "");
    setIsNameVerified(false);
    setDisablePan(false);
    if (!emailRegex.test(email)) return;

    if (abortCtrl) abortCtrl.abort();
    abortCtrl = new AbortController();

    let shouldDisablePan = false;

    try {
      const data: any = await prefillDetailsByAttribute({
        value: email,
        attribute_type: "email",
      });

      if (data?.is_found) {
        const user_data = data.user_data;
        const status_active = user_data?.onboarding_status === "active";
        setNotificationPopup(
          user_data?.onboarding_status === "active" ? "" : "registered",
        );

        if (!status_active) {
          form.setField("mobile_number", "");
          form.setField("aadhar_number", "");
          form.setField("address", "");
          form.setField("pan", "");
          form.setField("name", "");
          form.setField("gst_number", "");
          setIsNameVerified(false);
          return;
        }

        form.setField(
          "mobile_number",
          user_data?.mobile_number ? user_data.mobile_number.slice(3) : "",
        );
        if (non_individual_types.includes(bidder_type))
          form.setField("gst_number", user_data?.gst ?? "");
        else form.setField("aadhar_number", user_data?.aadhar ?? "");
        form.setField("address", user_data?.full_address ?? "");
        form.setField("pan", "");
        form.setField("name", "");
        setIsNameVerified(false);

        if (user_data?.pan) {
          form.setField("pan", user_data.pan);
          shouldDisablePan = true; // PAN found → disable

          // V4 mode: Don't auto-verify PAN from email prefetch
          // Wait for user to enter name first (3+ chars), then verify PAN
          // Legacy mode: Verify PAN immediately
          if (!isDigioPanFullVerificationEnabled) {
            const res: any = await fetchIDCardDetails({
              card_type: "PAN",
              card_number: user_data.pan,
            });
            await handlePANVerification(res, form, setIsNameVerified);
          }
        }
      } else {
        if (data?.is_found === false) setNotificationPopup("unregistered");
        // user not found → PAN should stay editable
        form.setField("mobile_number", "");
        if (non_individual_types.includes(bidder_type))
          form.setField("gst_number", "");
        else form.setField("aadhar_number", "");
        form.setField("pan", "");
        form.setField("name", "");
        form.setField("address", "");
        setIsNameVerified(false);
        shouldDisablePan = false;
      }
    } catch (err: any) {
      console.error("prefill error", err);
      shouldDisablePan = false; // API error → keep editable
    } finally {
      setDisablePan(shouldDisablePan);
      abortCtrl = null;
    }
  }, EMAIL_DEBOUNCE_MS);
};

const handleBackendValidations = async (
  form: any,
  event_uuid: string,
  setIsErrorHandlingModalOpen: any,
  setErrorHandlingModalData?: any,
) => {
  try {
    const payload = {
      email: form.getField("email"),
      mobile_number: form.getField("mobile_number"),
      pan: form.getField("pan"),
      name: form.getField("name"),
      user_type: form.getField("user_type"),
      event_uuid: event_uuid,
    };

    const res: any = await handleBackendValidationsForBidderCreation(payload);

    if (res && res?.is_valid) {
      return true;
    }

    return false;
  } catch (error: any) {
    console.error(error);

    if (error?.response?.data?.errors) {
      form.setErrors(error.response.data.errors);
    }

    const errorDetails =
      error?.response?.data?.message?.errorDetails ||
      error?.response?.data?.errorDetails;

    if (errorDetails) {
      let emailError = errorDetails?.email;
      let mobileError = errorDetails?.mobile;

      if (emailError && mobileError) {
        form.setErrors({
          email: [emailError],
          mobile_number: [mobileError],
        });

        if (emailError !== "Email already added for this event.") {
          if (setErrorHandlingModalData) {
            setErrorHandlingModalData([
              {
                email: emailError,
                mobile_number: mobileError,
              },
            ]);
          }
          setIsErrorHandlingModalOpen({ open: true, type: "api" });
        }
      } else if (emailError) {
        form.setErrors({
          email: [emailError],
        });
        if (emailError !== "Email already added for this event.") {
          if (setErrorHandlingModalData) {
            setErrorHandlingModalData([
              {
                email: emailError,
              },
            ]);
          }
          setIsErrorHandlingModalOpen({ open: true, type: "api" });
        }
      } else if (mobileError) {
        form.setErrors({
          mobile_number: [mobileError],
        });
        if (setErrorHandlingModalData) {
          setErrorHandlingModalData([
            {
              mobile_number: mobileError,
            },
          ]);
        }
        setIsErrorHandlingModalOpen({ open: true, type: "api" });
      }
    } else {
      // Unexpected error (e.g. 500, network failure) — show a generic message.
      if (setErrorHandlingModalData) {
        setErrorHandlingModalData([
          { message: "Something went wrong. Please try again." },
        ]);
      }
      setIsErrorHandlingModalOpen({ open: true, type: "api" });
    }

    return false;
  }
};

export {
  useGetBasicDetails,
  getTeamMembersEmailDetails,
  useInitializeBetaForms,
  handlePANVerification,
  handleAcceptBidder,
  handleRejectBidder,
  handlePANFieldValidations,
  kycDetailsSwitchFields,
  focusFieldById,
  handleBidderDataSubmit,
  validateBidders,
  handleSubmitAllBiddersData,
  bidderDataInitialise,
  saveCurrentBidderDataHelper,
  handleJointParticipationValidation,
  prefillDetailsByEmail,
  handleBackendValidations,
  isDigioPanFullVerificationEnabled,
};
